import { countries } from "countries-list"
import { ArrowLeft, ArrowRight, Briefcase, Building2, Check, ChevronsUpDown, Users } from "lucide-react"
import { Fragment, useState } from "react"
import { Link, useNavigate } from "react-router"

import HomeFooter from "@/components/home-footer"
import HomeNav from "@/components/home-nav"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { api, ApiError, setToken } from "@/lib/api"

const TECHNOLOGY_TYPES = [
  "Advanced Conversion Technologies",
  "Anaerobic Digestion",
  "Battery",
  "Biomass (co-firing)",
  "Biomass (dedicated)",
  "Compressed Air Energy Storage",
  "EfW Incineration",
  "Flywheels",
  "Fuel Cell (Hydrogen)",
  "Geothermal",
  "Hot Dry Rocks (HDR)",
  "Hydrogen",
  "Landfill Gas",
  "Large Hydro",
  "Liquid Air Energy Storage",
  "Pumped Storage Hydroelectricity",
  "Sewage Sludge Digestion",
  "Shoreline Wave",
  "Small Hydro",
  "Solar Photovoltaics",
  "Tidal Lagoon",
  "Tidal Stream",
  "Wind Offshore",
  "Wind Onshore",
]

const PRIORITY_COUNTRIES = ["United Kingdom", "United States"]
const COUNTRIES: { code: string; name: string }[] = [
  ...Object.entries(countries)
    .map(([code, data]) => ({ code, name: data.name }))
    .filter((c) => PRIORITY_COUNTRIES.includes(c.name))
    .sort((a, b) => PRIORITY_COUNTRIES.indexOf(a.name) - PRIORITY_COUNTRIES.indexOf(b.name)),
  ...Object.entries(countries)
    .map(([code, data]) => ({ code, name: data.name }))
    .filter((c) => !PRIORITY_COUNTRIES.includes(c.name))
    .sort((a, b) => a.name.localeCompare(b.name)),
]

type OnboardingStep = "welcome" | "account" | "organization" | "preferences" | "complete"

export default function OnboardingPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState<OnboardingStep>("welcome")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [countryOpen, setCountryOpen] = useState(false)
  const [techOpen, setTechOpen] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [apiError, setApiError] = useState("")
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    organizationType: "",
    organizationTypeOther: "",
    organizationName: "",
    organizationCountry: "",
    teamSize: "",
    interests: [] as string[],
  })

  const validateAccountStep = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.firstName.trim()) newErrors.firstName = "First name is required"
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required"
    if (!formData.email.trim()) {
      newErrors.email = "Email is required"
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address"
    }
    if (!formData.password) {
      newErrors.password = "Password is required"
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters"
    } else if (!/(?=.*[a-zA-Z])(?=.*[0-9])/.test(formData.password)) {
      newErrors.password = "Password must contain letters and numbers"
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password"
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match"
    }
    return newErrors
  }

  const validateOrganizationStep = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.organizationType) newErrors.organizationType = "Please select an organization type"
    if (formData.organizationType === "Other" && !formData.organizationTypeOther.trim()) newErrors.organizationTypeOther = "Please specify your organization type"
    if (!formData.organizationName.trim()) newErrors.organizationName = "Organisation name is required"
    if (!formData.teamSize) newErrors.teamSize = "Please select a team size"
    return newErrors
  }

  const validatePreferencesStep = () => {
    const newErrors: Record<string, string> = {}
    if (formData.interests.length === 0) newErrors.interests = "Please select at least one project type"
    return newErrors
  }

  const handleNext = async () => {
    let newErrors: Record<string, string> = {}
    if (step === "account") newErrors = validateAccountStep()
    else if (step === "organization") newErrors = validateOrganizationStep()
    else if (step === "preferences") newErrors = validatePreferencesStep()

    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) return

    if (step === "preferences") {
      setIsLoading(true)
      setApiError("")
      try {
        const payload = {
          account: {
            email: formData.email,
            first_name: formData.firstName,
            last_name: formData.lastName,
            password: formData.password,
            confirm_password: formData.confirmPassword,
            Photo_url: "",
          },
          organization: {
            name: formData.organizationName,
            type: formData.organizationType === "Other" ? formData.organizationTypeOther : formData.organizationType,
            team_size: formData.teamSize,
            country: formData.organizationCountry,
          },
          preferences: formData.interests,
        }
        const res = await api.post<{ statusCode: number; data: { token: string } }>("/auth/", payload)
        if (res.statusCode !== 201) {
          throw new Error("Registration failed. Please try again.")
        }
        setStep("complete")
      } catch (err) {
        setApiError(err instanceof ApiError ? err.message : "Registration failed. Please try again.")
      } finally {
        setIsLoading(false)
      }
      return
    }

    const steps: OnboardingStep[] = ["welcome", "account", "organization", "preferences", "complete"]
    const currentIndex = steps.indexOf(step)
    if (currentIndex < steps.length - 1) setStep(steps[currentIndex + 1])
  }

  const handleBack = () => {
    setErrors({})
    const steps: OnboardingStep[] = ["welcome", "account", "organization", "preferences", "complete"]
    const currentIndex = steps.indexOf(step)
    if (currentIndex > 0) setStep(steps[currentIndex - 1])
  }

  const handleComplete = () => navigate("/dashboard")

  return (
    <>
      <HomeNav />

      {/* Split layout */}
      <div className="flex min-h-[calc(100svh-4rem)]">

        {/* Left — Form panel */}
        <div className="flex-1 flex items-center justify-center bg-white px-6 py-16 sm:px-12 overflow-y-auto">
          <div className="w-full max-w-md">

            {/* Progress Indicator */}
            {step !== "welcome" && step !== "complete" && (
              <div className="mb-14">
                <div className="flex items-center">
                  {["Account", "Organisation", "Preferences"].map((label, index) => {
                    const steps: OnboardingStep[] = ["account", "organization", "preferences"]
                    const currentIndex = steps.indexOf(step)
                    const isActive = index === currentIndex
                    const isComplete = index < currentIndex
                    return (
                      <Fragment key={label}>
                        <div className="flex flex-col items-center gap-2">
                          <div
                            className={`w-9 h-9 flex items-center justify-center text-sm font-bold transition-colors duration-200 ${isActive
                              ? "bg-[#0D0D0D] text-white"
                              : isComplete
                                ? "bg-[#1D591B] text-white"
                                : "bg-[#0D0D0D]/8 text-[#0D0D0D]/35"
                              }`}
                          >
                            {index + 1}
                          </div>
                          <span
                            className={`text-[11px] font-semibold uppercase tracking-widest whitespace-nowrap transition-colors duration-200 ${isActive ? "text-[#0D0D0D]" : isComplete ? "text-[#1D591B]" : "text-[#0D0D0D]/30"
                              }`}
                          >
                            {label}
                          </span>
                        </div>
                        {index < 2 && (
                          <div
                            className={`flex-1 h-px mx-4 mb-5 transition-colors duration-200 ${isComplete ? "bg-[#1D591B]" : "bg-[#0D0D0D]/10"
                              }`}
                          />
                        )}
                      </Fragment>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Welcome Step */}
            {step === "welcome" && (
              <div>
                <div className="w-10 h-0.75 bg-secondary mb-8 rounded-full" />
                <h1 className="text-4xl font-black text-[#0D0D0D] tracking-tight leading-[1.05] mb-3">
                  {"Welcome to Kanu Energy"}
                </h1>
                <p className="text-[#0D0D0D]/55 text-base leading-relaxed mb-10">
                  {"Join the UK's first unified intelligence platform for renewable energy investments."}
                </p>
                <div className="grid gap-px bg-[#0D0D0D]/8 mb-10">
                  <div className="bg-white flex items-start gap-5 p-6">
                    <Building2 className="h-5 w-5 text-[#1D591B] mt-0.5 shrink-0" />
                    <div>
                      <h4 className="font-semibold text-[#0D0D0D] text-sm mb-1">{"Unified Data Access"}</h4>
                      <p className="text-sm text-[#0D0D0D]/55 leading-relaxed">
                        {"Access 125 GW connection queue data from all DNO regions in one platform"}
                      </p>
                    </div>
                  </div>
                  <div className="bg-white flex items-start gap-5 p-6">
                    <Briefcase className="h-5 w-5 text-[#1D591B] mt-0.5 shrink-0" />
                    <div>
                      <h4 className="font-semibold text-[#0D0D0D] text-sm mb-1">{"AI-Powered Insights"}</h4>
                      <p className="text-sm text-[#0D0D0D]/55 leading-relaxed">
                        {"Ask questions in natural language and get instant, cited answers"}
                      </p>
                    </div>
                  </div>
                  <div className="bg-white flex items-start gap-5 p-6">
                    <Users className="h-5 w-5 text-[#1D591B] mt-0.5 shrink-0" />
                    <div>
                      <h4 className="font-semibold text-[#0D0D0D] text-sm mb-1">{"Gate 2 Predictions"}</h4>
                      <p className="text-sm text-[#0D0D0D]/55 leading-relaxed">
                        {"Project-level readiness scoring to identify high-potential investments"}
                      </p>
                    </div>
                  </div>
                </div>
                <Button
                  className="w-full rounded-none bg-primary hover:bg-foreground text-white h-12 font-semibold text-sm tracking-wide transition-colors duration-200 cursor-pointer"
                  onClick={handleNext}
                >
                  {"Get Started"} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <p className="text-center text-sm text-[#0D0D0D]/45 mt-6">
                  {"Already have an account? "}
                  <Link to="/login" className="text-[#1D591B] hover:underline font-medium">
                    {"Sign in"}
                  </Link>
                </p>
              </div>
            )}

            {/* Account Step */}
            {step === "account" && (
              <div>
                <div className="w-10 h-0.75 bg-secondary mb-8 rounded-full" />
                <h2 className="text-3xl font-black text-[#0D0D0D] tracking-tight mb-2">{"Create your account"}</h2>
                <p className="text-[#0D0D0D]/50 text-sm leading-relaxed mb-10">
                  {"Set up your personal credentials to get started."}
                </p>
                <div className="space-y-5">
                  <div className="space-y-1.5">
                    <Label htmlFor="firstName" className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60">
                      {"First Name"} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="firstName" placeholder="John" value={formData.firstName}
                      onChange={(e) => { setFormData({ ...formData, firstName: e.target.value }); if (errors.firstName) setErrors({ ...errors, firstName: "" }) }}
                      className="rounded-none h-11 border-[#0D0D0D]/20 focus-visible:border-[#1D591B] focus-visible:ring-0"
                      aria-invalid={!!errors.firstName}
                    />
                    {errors.firstName && <p className="text-xs text-destructive">{errors.firstName}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="lastName" className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60">
                      {"Last Name"} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="lastName" placeholder="Smith" value={formData.lastName}
                      onChange={(e) => { setFormData({ ...formData, lastName: e.target.value }); if (errors.lastName) setErrors({ ...errors, lastName: "" }) }}
                      className="rounded-none h-11 border-[#0D0D0D]/20 focus-visible:border-[#1D591B] focus-visible:ring-0"
                      aria-invalid={!!errors.lastName}
                    />
                    {errors.lastName && <p className="text-xs text-destructive">{errors.lastName}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60">
                      {"Work Email"} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="email" type="email" placeholder="john.smith@company.com" value={formData.email}
                      onChange={(e) => { setFormData({ ...formData, email: e.target.value }); if (errors.email) setErrors({ ...errors, email: "" }) }}
                      className="rounded-none h-11 border-[#0D0D0D]/20 focus-visible:border-[#1D591B] focus-visible:ring-0"
                      aria-invalid={!!errors.email}
                    />
                    {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60">
                      {"Password"} <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="password" type={showPassword ? "text" : "password"} placeholder="••••••••" value={formData.password} autoComplete="new-password"
                        onChange={(e) => { setFormData({ ...formData, password: e.target.value }); if (errors.password) setErrors({ ...errors, password: "" }) }}
                        className="rounded-none h-11 border-[#0D0D0D]/20 focus-visible:border-[#1D591B] focus-visible:ring-0 pr-10"
                        aria-invalid={!!errors.password}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0D0D0D]/35 hover:text-[#0D0D0D]/60 transition-colors"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <span className="text-xs font-medium">{showPassword ? "Hide" : "Show"}</span>
                      </button>
                    </div>
                    {errors.password ? (
                      <p className="text-xs text-destructive">{errors.password}</p>
                    ) : (
                      <p className="text-xs text-[#0D0D0D]/40">
                        {"Must be at least 8 characters with a mix of letters and numbers"}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="confirmPassword" className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60">
                      {"Confirm Password"} <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword" type={showConfirmPassword ? "text" : "password"} placeholder="••••••••" value={formData.confirmPassword} autoComplete="new-password"
                        onChange={(e) => { setFormData({ ...formData, confirmPassword: e.target.value }); if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: "" }) }}
                        className="rounded-none h-11 border-[#0D0D0D]/20 focus-visible:border-[#1D591B] focus-visible:ring-0 pr-10"
                        aria-invalid={!!errors.confirmPassword}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0D0D0D]/35 hover:text-[#0D0D0D]/60 transition-colors"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        <span className="text-xs font-medium">{showConfirmPassword ? "Hide" : "Show"}</span>
                      </button>
                    </div>
                    {errors.confirmPassword && <p className="text-xs text-destructive">{errors.confirmPassword}</p>}
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      className="flex-1 rounded-none border-[#0D0D0D]/20 bg-transparent text-[#0D0D0D] hover:border-[#0D0D0D] hover:bg-transparent hover:text-primary h-11"
                      onClick={handleBack}
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" /> {"Back"}
                    </Button>
                    <Button
                      className="flex-1 rounded-none bg-primary hover:bg-foreground text-white h-11 font-semibold transition-colors duration-200 cursor-pointer"
                      onClick={handleNext}
                    >
                      {"Continue"} <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-center text-sm text-[#0D0D0D]/45">
                    {"Already have an account? "}
                    <Link to="/login" className="text-[#1D591B] hover:underline font-medium">{"Sign in"}</Link>
                  </p>
                </div>
              </div>
            )}

            {/* Organisation Step */}
            {step === "organization" && (
              <div>
                <div className="w-10 h-0.75 bg-secondary mb-8 rounded-full" />
                <h2 className="text-3xl font-black text-[#0D0D0D] tracking-tight mb-2">
                  {"Tell us about your organisation"}
                </h2>
                <p className="text-[#0D0D0D]/50 text-sm leading-relaxed mb-10">
                  {"Help us customize your experience based on your needs."}
                </p>
                <div className="space-y-8">
                  <div className="space-y-3">
                    <Label className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60">
                      {"Organisation Type"} <span className="text-destructive">*</span>
                    </Label>
                    <RadioGroup
                      value={formData.organizationType}
                      onValueChange={(value) => { setFormData({ ...formData, organizationType: value, organizationTypeOther: "" }); if (errors.organizationType) setErrors({ ...errors, organizationType: "", organizationTypeOther: "" }) }}
                      className="space-y-2"
                    >
                      {[
                        { value: "Investment Firm / M&A Team", id: "investor" },
                        { value: "Renewable Energy Developer", id: "developer" },
                        { value: "Technical Due Diligence Firm", id: "consultant" },
                        { value: "Energy Analyst / Researcher", id: "analyst" },
                        { value: "Other", id: "other" },
                      ].map(({ value, id }) => (
                        <div key={id} className="flex items-center space-x-3 p-4 border border-[#0D0D0D]/12 hover:border-[#1D591B]/50 hover:bg-[#1D591B]/3 cursor-pointer transition-colors duration-150">
                          <RadioGroupItem value={value} id={id} />
                          <Label htmlFor={id} className="flex-1 cursor-pointer text-sm font-medium text-[#0D0D0D]">{value}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                    {formData.organizationType === "Other" && (
                      <Input
                        placeholder="Please specify your organization type"
                        value={formData.organizationTypeOther}
                        onChange={(e) => { setFormData({ ...formData, organizationTypeOther: e.target.value }); if (errors.organizationTypeOther) setErrors({ ...errors, organizationTypeOther: "" }) }}
                        className="mt-2"
                      />
                    )}
                    {errors.organizationType && <p className="text-xs text-destructive">{errors.organizationType}</p>}
                    {errors.organizationTypeOther && <p className="text-xs text-destructive">{errors.organizationTypeOther}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="organizationName" className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60">
                      {"Organisation Name"} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="organizationName" placeholder="Your company name" value={formData.organizationName}
                      onChange={(e) => { setFormData({ ...formData, organizationName: e.target.value }); if (errors.organizationName) setErrors({ ...errors, organizationName: "" }) }}
                      className="rounded-none h-11 border-[#0D0D0D]/20 focus-visible:border-[#1D591B] focus-visible:ring-0"
                      aria-invalid={!!errors.organizationName}
                    />
                    {errors.organizationName && <p className="text-xs text-destructive">{errors.organizationName}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60">
                      {"Organisation Country"} <span className="text-destructive">*</span>
                    </Label>
                    <Popover open={countryOpen} onOpenChange={setCountryOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={countryOpen}
                          className={`w-full h-11 rounded-none border-[#0D0D0D]/20 bg-transparent hover:bg-transparent justify-between font-normal text-sm px-3 ${countryOpen ? "border-[#1D591B]" : ""} ${!formData.organizationCountry ? "text-[#0D0D0D]/40" : "text-[#0D0D0D]"}`}
                        >
                          {formData.organizationCountry
                            ? COUNTRIES.find((c) => c.name === formData.organizationCountry)?.name
                            : "Select country"}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-(--radix-popover-trigger-width) p-0 rounded-none" align="start">
                        <Command>
                          <CommandInput placeholder="Search country..." className="h-9" />
                          <CommandList>
                            <CommandEmpty>{"No country found."}</CommandEmpty>
                            <CommandGroup>
                              {COUNTRIES.map((country) => (
                                <CommandItem
                                  key={country.code}
                                  value={country.name}
                                  onSelect={(val) => {
                                    setFormData({ ...formData, organizationCountry: val })
                                    if (errors.organizationCountry) setErrors({ ...errors, organizationCountry: "" })
                                    setCountryOpen(false)
                                  }}
                                >
                                  {country.name}
                                  {formData.organizationCountry === country.name && (
                                    <Check className="ml-auto h-4 w-4 text-[#1D591B]" />
                                  )}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {errors.organizationCountry && <p className="text-xs text-destructive">{errors.organizationCountry}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60">
                      {"Team Size"} <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={formData.teamSize}
                      onValueChange={(value) => { setFormData({ ...formData, teamSize: value }); if (errors.teamSize) setErrors({ ...errors, teamSize: "" }) }}
                    >
                      <SelectTrigger className="rounded-none h-24 border-[#0D0D0D]/20 focus:border-[#1D591B] focus:ring-0 data-[state=open]:border-[#1D591B] text-sm">
                        <SelectValue placeholder="Select team size" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {["1-5", "6-20", "21-50", "51+"].map((size) => (
                          <SelectItem key={size} value={size} className="text-sm cursor-pointer">{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.teamSize && <p className="text-xs text-destructive">{errors.teamSize}</p>}
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="flex-1 rounded-none border-[#0D0D0D]/20 bg-transparent text-[#0D0D0D] hover:border-[#0D0D0D] hover:bg-transparent hover:text-primary h-11"
                      onClick={handleBack}
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" /> {"Back"}
                    </Button>
                    <Button
                      className="flex-1 rounded-none bg-primary hover:bg-foreground text-white h-11 font-semibold transition-colors duration-200 cursor-pointer"
                      onClick={handleNext}
                    >
                      {"Continue"} <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Preferences Step */}
            {step === "preferences" && (
              <>
                <div>
                  <div className="w-10 h-0.75 bg-secondary mb-8 rounded-full" />
                  <h2 className="text-3xl font-black text-[#0D0D0D] tracking-tight mb-2">
                    {"Customize your experience"}
                  </h2>
                  <p className="text-[#0D0D0D]/50 text-sm leading-relaxed mb-10">
                    {"Select the types of projects and regions you're most interested in tracking."}
                  </p>
                  <div className="space-y-8">
                    <div className="space-y-3">
                      <Label className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60">
                        {"Project Types of Interest"} <span className="text-destructive">*</span>
                      </Label>
                      <Popover open={techOpen} onOpenChange={setTechOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={techOpen}
                            className={`w-full min-h-11 h-auto rounded-none border-[#0D0D0D]/20 bg-transparent hover:bg-transparent justify-between font-normal text-sm px-3 py-2.5 ${techOpen ? "border-[#1D591B]" : ""} ${formData.interests.length === 0 ? "text-[#0D0D0D]/40" : "text-[#0D0D0D]"}`}
                          >
                            <span className="flex flex-wrap gap-1.5">
                              {formData.interests.length === 0
                                ? "Select technology types"
                                : formData.interests.map((t) => (
                                    <span key={t} className="inline-flex items-center bg-[#1D591B]/10 text-[#1D591B] text-xs px-2 py-0.5 font-medium">
                                      {t}
                                    </span>
                                  ))}
                            </span>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-(--radix-popover-trigger-width) p-0 rounded-none" align="start">
                          <Command>
                            <CommandInput placeholder="Search technology..." className="h-9" />
                            <CommandList className="max-h-60">
                              <CommandEmpty>{"No technology found."}</CommandEmpty>
                              <CommandGroup>
                                {TECHNOLOGY_TYPES.map((tech) => (
                                  <CommandItem
                                    key={tech}
                                    value={tech}
                                    onSelect={() => {
                                      const next = formData.interests.includes(tech)
                                        ? formData.interests.filter((i) => i !== tech)
                                        : [...formData.interests, tech]
                                      setFormData({ ...formData, interests: next })
                                      if (errors.interests) setErrors({ ...errors, interests: "" })
                                    }}
                                  >
                                    <Checkbox
                                      checked={formData.interests.includes(tech)}
                                      className="mr-2 pointer-events-none"
                                    />
                                    {tech}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      {errors.interests && <p className="text-xs text-destructive">{errors.interests}</p>}
                    </div>
                    {apiError && <p className="text-sm text-destructive">{apiError}</p>}
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        className="flex-1 rounded-none border-[#0D0D0D]/20 bg-transparent text-[#0D0D0D] hover:border-[#0D0D0D] hover:bg-transparent hover:text-primary h-11"
                        onClick={handleBack} disabled={isLoading}
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" /> {"Back"}
                      </Button>
                      <Button
                        className="flex-1 rounded-none bg-primary hover:bg-foreground text-white h-11 font-semibold transition-colors duration-200 cursor-pointer"
                        onClick={handleNext} disabled={isLoading}
                      >
                        {isLoading ? "Creating account…" : "Complete Setup"}
                        {!isLoading && <ArrowRight className="ml-2 h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
                <p className="text-center text-xs text-[#0D0D0D]/40 mt-10">
                  {"By continuing, you agree to Kanu Energy's "}
                  <Link to="#" className="underline hover:text-[#0D0D0D]/60">Terms of Service</Link>
                  {" and "}
                  <Link to="#" className="underline hover:text-[#0D0D0D]/60">Privacy Policy</Link>
                </p>
              </>
            )}

            {/* Complete Step */}
            {step === "complete" && (
              <div>
                <div className="w-10 h-0.75 bg-secondary mb-8 rounded-full" />
                <h2 className="text-4xl font-black text-[#0D0D0D] tracking-tight leading-[1.05] mb-3">
                  {"Check your inbox"}
                </h2>
                <p className="text-[#0D0D0D]/55 text-base leading-relaxed mb-10">
                  {"We've sent a verification link to "}
                  <span className="font-semibold text-[#0D0D0D]/80">{formData.email}</span>
                  {". Click the link in your email to activate your account."}
                </p>
                <div className="border border-[#0D0D0D]/10 p-8 mb-8">
                  <h4 className="font-bold text-[#0D0D0D] mb-4 text-xs uppercase tracking-widest">{"Didn't receive it?"}</h4>
                  <ul className="space-y-3">
                    {[
                      "Check your spam or junk folder",
                      "Make sure you entered the correct email address",
                      "Allow a few minutes for the email to arrive",
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-4">
                        <span className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 shrink-0" />
                        <span className="text-sm text-[#0D0D0D]/65 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-center text-sm text-[#0D0D0D]/45">
                  {"Wrong email? "}
                  <button
                    type="button"
                    className="text-[#1D591B] hover:underline font-medium"
                    onClick={() => setStep("account")}
                  >
                    {"Go back and update it"}
                  </button>
                </p>
              </div>
            )}

          </div>
        </div>

        {/* Right — Brand panel */}
        <div className="hidden lg:flex w-[48%] bg-[url('/grid.jpg')] bg-cover bg-center bg-no-repeat relative overflow-hidden">
        </div>

      </div>

      <HomeFooter />
    </>
  )
}
